import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Copy, Eye, EyeOff, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { api, asList, errMsg } from "@/lib/api";
import { mediaUrl } from "@/lib/site";
import { move } from "./site/Images";
import { ProductEditor } from "./ProductEditor";

const BLANK = { name: "", category: "Apparel", price: "TBA", sku: "", status: "COMING SOON", description: "", description_pt: "", sizes: [], visible: true, image: "", images: [] };
const strip = ({ id, created_at, ...rest }) => rest;

const IconBtn = ({ onClick, label, testid, danger, children }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className={`p-2.5 border border-[var(--line)] transition-colors ${danger ? "hover:border-red-400 hover:text-red-400" : "hover:border-[var(--bone)]"}`}>{children}</button>
);

export const ProductsManager = () => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [drag, setDrag] = useState(null);
  const load = () => api.get("/products").then((r) => setRows(asList(r))).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    const body = strip(p);
    try {
      if (p.id) await api.put(`/products/${p.id}`, body);
      else await api.post("/products", { ...body, order: rows.length });
      toast.success("Product saved — live on the website");
      setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const update = async (p, patch) => {
    try { await api.put(`/products/${p.id}`, { ...strip(p), ...patch }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const duplicate = (p) => setEdit({ ...strip(p), name: `${p.name} (copy)`, sku: "" });
  const reorder = async (from, to) => {
    if (to < 0 || to >= rows.length || from === to) return;
    const next = move(rows, from, to);
    setRows(next);
    try { await api.put("/products/reorder", { ids: next.map((r) => r.id) }); } catch (e) { toast.error(errMsg(e)); load(); }
  };

  return (
    <div className="mt-6" data-testid="products-manager">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-[var(--muted)]">{rows.length} product{rows.length === 1 ? "" : "s"} · drag cards to change the order on the website. The first one is featured.</p>
        <button data-testid="product-add-button" onClick={() => setEdit({ ...BLANK })}
          className="inline-flex items-center gap-2 clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[var(--bone)]">
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>

      {rows.length === 0 && <p className="py-12 text-sm text-[var(--muted)]">No products yet. Click "Add product" to create your first one.</p>}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p, i) => (
          <div key={p.id} data-testid={`product-admin-card-${p.id}`} draggable
            onDragStart={() => setDrag(i)}
            onDragOver={(e) => { if (drag !== null) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); if (drag !== null) reorder(drag, i); setDrag(null); }}
            onDragEnd={() => setDrag(null)}
            className={`border bg-[var(--ink-2)] ${drag === i ? "border-white opacity-50" : "border-[var(--line)]"} ${p.visible === false ? "opacity-60" : ""}`}>
            <div className="relative aspect-[4/3] overflow-hidden bg-[var(--ink-3)]">
              {p.image && <img src={mediaUrl(p.image)} alt="" className="w-full h-full object-cover pointer-events-none" />}
              <span className="absolute top-2 left-2 flex items-center gap-1 font-mono text-[10px] bg-black/80 px-1.5 py-1 cursor-grab"><GripVertical className="w-3 h-3" />{i === 0 ? "FEATURED" : `#${i + 1}`}</span>
              <span className="absolute top-2 right-2 font-mono text-[10px] tracking-[0.15em] px-2 py-1 bg-[var(--bone)] text-black">{p.status}</span>
              {p.visible === false && <span className="absolute bottom-2 left-2 font-mono text-[10px] bg-black/80 px-2 py-1">HIDDEN</span>}
            </div>
            <div className="p-4">
              <p className="font-display text-2xl font-bold uppercase leading-none">{p.name}</p>
              <p className="font-mono text-[11px] text-[var(--muted)] uppercase tracking-[0.15em] mt-2">
                {[p.sku, p.category, /^\d/.test(p.price || "") ? `${p.price} €` : p.price, p.sizes?.length ? p.sizes.join("/") : ""].filter(Boolean).join(" · ")}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <IconBtn onClick={() => setEdit(p)} label="Edit" testid={`product-edit-${p.id}`}><Pencil className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => duplicate(p)} label="Duplicate" testid={`product-duplicate-${p.id}`}><Copy className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => update(p, { visible: p.visible === false })} label={p.visible === false ? "Show on website" : "Hide from website"} testid={`product-visibility-${p.id}`}>
                  {p.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </IconBtn>
                <IconBtn onClick={() => reorder(i, i - 1)} label="Move earlier" testid={`product-up-${p.id}`}><ArrowUp className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => reorder(i, i + 1)} label="Move later" testid={`product-down-${p.id}`}><ArrowDown className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => remove(p)} label="Delete" testid={`product-delete-${p.id}`} danger><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            </div>
          </div>
        ))}
      </div>

      {edit && <ProductEditor key={edit.id || "new"} product={edit} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
};
